/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { IBankAccount, ICard, Invoice, InvoiceFailure, InvoiceItem } from '@core/models';
import { createAction, props } from '@ngrx/store';

export const simulateInvoiceGeneration = createAction(
  '[ClientDetails:Dashboard] Simulate Invoice Generation',
  props<{ clientId: number }>()
);
export const simulateInvoiceGenerationSuccess = createAction(
  '[ClientDetails:Dashboard] Simulate Invoice Generation Success'
);
export const simulateInvoiceGenerationFailure = createAction(
  '[ClientDetails:Dashboard] Simulate Invoice Generation Failure',
  props<{ errorMessage: string }>()
);

export const loadInvoices = createAction('[ClientDetails] Load Invoices', props<{ clientId: number }>());
export const loadInvoicesSuccess = createAction(
  '[ClientDetails] Load Invoices Success',
  props<{ invoices: Invoice[]; paymentMethods: (IBankAccount & ICard)[] }>()
);
export const loadInvoicesFailure = createAction(
  '[ClientDetails] Load Invoices Failure',
  props<{ errorMessage: string }>()
);

export const sendInvoice = createAction('[ClientDetails:Billing] Send Invoice', props<{ invoice: Invoice }>());
export const sendInvoiceSuccess = createAction(
  '[ClientDetails:Billing] Send Invoice Success',
  props<{ invoice: Invoice }>()
);
export const sendInvoiceFailure = createAction(
  '[ClientDetails:Billing] Send Invoice Failure',
  props<{ errorMessage: string }>()
);
export const reSendInvoice = createAction('[ClientDetails:Billing] Re-send Invoice', props<{ invoice: Invoice }>());

export const deleteInvoice = createAction('[ClientDetails:Billing] Delete Invoice', props<{ invoice: Invoice }>());

export const deleteInvoiceSuccess = createAction(
  '[ClientDetails:Billing] Delete Invoice Success',
  props<{ id: number }>()
);
export const deleteInvoiceFailure = createAction(
  '[ClientDetails:Billing] Delete Invoice Failure',
  props<{ errorMessage: string }>()
);

export const generatePDF = createAction('[ClientDetails:Billing] Generate PDF', props<{ invoice: Invoice }>());

export const generatePDFSuccess = createAction(
  '[ClientDetails:Billing] Generate PDF Success',
  props<{ invoice: Invoice }>()
);

export const generatePDFFailure = createAction(
  '[ClientDetails:Billing] Generate PDF Failure',
  props<{ errorMessage: string }>()
);

export const updateInvoice = createAction(
  '[ClientDetails:Billing] Update Invoice',
  props<{
    invoice: Invoice;
    itemsToAdd: InvoiceItem[];
    itemsToRemove: InvoiceItem[];
  }>()
);

export const updateInvoiceSuccess = createAction(
  '[ClientDetails:Billing] Update Invoice Success',
  props<{ deletedInvoiceId: number | null; invoice: Invoice; invoiceItems: InvoiceItem[] }>()
);

export const updateInvoiceFailure = createAction(
  '[ClientDetails:Billing] Update Invoice Failure',
  props<{ errorMessage: string }>()
);

export const applyDiscount = createAction(
  '[ClientDetails:Invoice Details] Apply Discount',
  props<{ invoiceId: number; code: string }>()
);
export const applyDiscountSuccess = createAction(
  '[ClientDetails:Invoice Details] Apply Discount Success',
  props<{ invoice: Invoice }>()
);
export const applyDiscountFailure = createAction(
  '[ClientDetails:Invoice Details] Apply Discount Failure',
  props<{ errorMessage: string }>()
);

export const newAddonInvoice = createAction(
  '[ClientDetails:AddOn] New Addon Invoice',
  props<{ invoice: Invoice | null }>()
);

export const loadInvoiceFailures = createAction(
  '[ClientDetails:Billing] Load Invoice Failures',
  props<{ clientId: number }>()
);
export const loadInvoiceFailuresSuccess = createAction(
  '[ClientDetails:Billing] Load Invoice Failures Success',
  props<{ invoiceFailures: InvoiceFailure[] }>()
);
export const loadInvoiceFailuresFailure = createAction(
  '[ClientDetails:Billing] Load Invoice Failures Failure',
  props<{ errorMessage: string }>()
);

export const rerunInvoiceFailures = createAction(
  '[ClientDetails:Billing] Rerun Invoice Failures',
  props<{ invoiceFailures: InvoiceFailure[]; clientId: number }>()
);
export const rerunInvoiceFailuresSuccess = createAction(
  '[ClientDetails:Billing] Rerun Invoice Failures Success',
  props<{ invoices: Invoice[]; failures: InvoiceFailure[] }>()
);
export const rerunInvoiceFailuresFailure = createAction(
  '[ClientDetails:Billing] Rerun Invoice Failures Failure',
  props<{ errorMessage: string }>()
);

export const openInvoiceDetailsModal = createAction(
  '[ClientDetails:Billing] Open Invoice Details Modal',
  props<{ invoiceId?: number; invoice?: Invoice }>()
);
